import React, { useEffect, useRef, useState } from "react";
import "./language.css";
import { useDispatch } from "react-redux";
import { setLanguage } from "../../reduxSlices/languageSlices";
import {
  getLanguageOfUser,
  isMobile,
  setLanguageOfUser,
} from "../../store/user";
import { useTranslation } from "react-i18next";
import { getWidgetSettings } from "../../serivces/widgetService";

interface LanguageProps {
  isLanguageOpen: (isOpen: boolean) => void;
}

interface LanguageList {
  id: number;
  name: string;
  code: string;
  url: string;
}

function Language({ isLanguageOpen }: LanguageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const dispatch = useDispatch();

  const [allLanguages, setAllLanguages] = useState<LanguageList[]>([]);
  const [filteredLanguageList, setFilteredLanguageList] = useState<
    LanguageList[]
  >([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [loader, setLoader] = useState(false);

  const { i18n, t } = useTranslation();

  const LANGUAGES: LanguageList[] = [
    {
      id: 1,
      name: "German",
      code: "de",
      url: "https://widget.incluway.de/language/germanDEIcon.svg",
    },
    {
      id: 2,
      name: "English",
      code: "en",
      url: "https://widget.incluway.de/language/englishUKIcon.svg",
    },
  ];

  useEffect(() => {
    const filteredList = allLanguages.filter((language) =>
      language.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    setFilteredLanguageList(filteredList);
  }, [searchQuery, allLanguages]);

  useEffect(() => {
    if (selectedLanguage) {
      dispatch(setLanguage(selectedLanguage));
    }
  }, [selectedLanguage, dispatch]);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      setLoader(true);

      setAllLanguages(LANGUAGES);
      setFilteredLanguageList(LANGUAGES);

      const storedLang = getLanguageOfUser();
      const response = await getWidgetSettings();

      const backendDefault =
        response?.settings?.advanced?.defaultLanguage || "";

      let resolvedLanguage = LANGUAGES[1]; // English fallback

      // 1. user selection from localStorage always wins
      if (
        storedLang?.code === "de" ||
        storedLang?.name === "German" ||
        storedLang?.name === "Deutsch"
      ) {
        resolvedLanguage = LANGUAGES[0];
      } else if (storedLang?.code === "en" || storedLang?.name === "English") {
        resolvedLanguage = LANGUAGES[1];
      }
      // 2. otherwise use backend default
      else if (
        backendDefault === "German" ||
        backendDefault === "Deutsch" ||
        backendDefault === "de"
      ) {
        resolvedLanguage = LANGUAGES[0];
      } else if (backendDefault === "English" || backendDefault === "en") {
        resolvedLanguage = LANGUAGES[1];
      }

      setSelectedLanguage(resolvedLanguage.name);
      setLanguageOfUser(resolvedLanguage);
      dispatch(setLanguage(resolvedLanguage.name));
      await changeLang(resolvedLanguage.code);
    } catch (error) {
      console.error("Failed to fetch languages:", error);

      setAllLanguages(LANGUAGES);
      setFilteredLanguageList(LANGUAGES);

      const fallbackLanguage = LANGUAGES[1];

      setSelectedLanguage(fallbackLanguage.name);
      setLanguageOfUser(fallbackLanguage);
      dispatch(setLanguage(fallbackLanguage.name));
      await changeLang(fallbackLanguage.code);
    } finally {
      setLoader(false);
    }
  };

  const handleLanguageSelection = (language: LanguageList) => {
    setSelectedLanguage(language.name);
    setLanguageOfUser(language);
    changeLang(language.code);
    dispatch(setLanguage(language.name));
    isLanguageOpen(false);
  };

  const changeLang = async (language: string) => {
    await i18n.changeLanguage(language);
  };

  const handleLanguageClose = () => {
    isLanguageOpen(false);
  };

  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!loader && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [loader]);

  return (
    <div className="absolute top-0 left-0 w-[100%] h-[100%] flex items-center justify-between bg-language-background rounded-[0.75rem] launguageMainContainer">
      <div className="flex flex-col h-[85%] w-[100%] ml-[1.5rem] mr-[1.5rem] bg-primary-background rounded-[0.75rem] z-10 launguageContainer">
        {loader && (
          <div
            style={{
              backgroundImage: `url(${"/dictionary/search_loader.svg"})`,
            }}
            className="w-[2rem] h-[2rem] animate-spin [animation-duration:3s] bg-no-repeat bg-center bg-contain flex items-center justify-center m-auto"
          />
        )}

        {!loader && (
          <div className="flex flex-col items-center justify-center">
            <div className="flex flex-row justify-between w-[100%] pt-[1.38rem] pb-[1.63rem] pl-[0.87rem] pr-[0.87rem]">
              <div />
              <div className="flex flex-row w-[100%] justify-center text-secondary font-[600] text-secondary-background leading-[1.375rem]">
                {t("Choose Language")}
              </div>
              <button
                ref={closeButtonRef}
                className="flex flex-row items-center justify-center w-[1.75rem] h-[1.75rem] aspect-square flex-shrink-0 hover:bg-[#f4f4f4] rounded-[0.313rem] cursor-pointer searchIconPress"
                onClick={handleLanguageClose}
              >
                <img
                  src="/language/close_vector.svg"
                  alt="close Icon"
                  className="w-[15.34px] h-[15.34px]"
                />
              </button>
            </div>

            <div className="w-[calc(100%-2rem)] mb-[0.75rem] h-[3.25rem] pl-[1.06rem] pr-[0.938rem] flex flex-row items-center justify-between border border-inner-widget-border rounded-language-border-radius hover:border-widget-background focus-within:border-widget-background">
              <input
                type="text"
                className="w-[90%] h-[100%] pr-[0.69rem] text-primary font-[400] outline-none rounded-language-border-radius text-secondary-background"
                placeholder={t("Search language")}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="w-[1.5rem] h-[1.5rem] flex items-center justify-center">
                <div
                  style={{
                    backgroundImage: `url('/language/searchIcon.svg')`,
                  }}
                  className="w-[100%] h-[100%] bg-no-repeat bg-center bg-contain"
                />
              </div>
            </div>

            <div className="border-t border-inner-widget-border w-[95%] h-[0.0625rem] pl-[0.5rem] pr-[0.5rem] mb-[1.19rem] flex items-center justify-center m-auto" />

            <div
              className={`flex-1 w-full ${
                filteredLanguageList.length > 0
                  ? "customScrollBarLanguage overflow-y-auto"
                  : ""
              }`}
            >
              {filteredLanguageList.length === 0 ? (
                <div className="flex items-center justify-center h-[100%]">
                  <div className="text-secondary-background flex flex-col leading-[1.125rem] items-center justify-center text-primary font-[400]">
                    <div>Sorry, language isn&apos;t available yet.</div>
                    <div>Please select other option from the list.</div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-[1.25rem] pl-[1rem] pr-[1rem] pb-[1rem]">
                  {filteredLanguageList.map((language) => (
                    <button
                      key={language.id}
                      className={`flex items-center pt-[0.9375rem] pr-[1.125rem] pb-[0.9375rem] pl-[1.125rem] gap-[0.69rem] rounded-[0.438rem] cursor-pointer bg-setting-card transition-all duration-300 ${
                        selectedLanguage === language.name
                          ? "bg-widget-background"
                          : "hover:bg-language-list-hover"
                      }`}
                      onClick={() => handleLanguageSelection(language)}
                    >
                      <div
                        style={{ backgroundImage: `url(${language.url})` }}
                        className="w-[1.75rem] h-[1.75rem] bg-no-repeat bg-center bg-contain launguageImage"
                      />
                      <div
                        title={language.name}
                        className={`text-primary text-nowrap font-[400] leading-[1.125rem] overflow-hidden text-ellipsis ${
                          selectedLanguage === language.name
                            ? "text-white"
                            : "text-secondary-background"
                        }`}
                      >
                        {language.name.length > (isMobile() ? 8 : 15)
                          ? `${language.name.slice(0, isMobile() ? 7 : 15)}...`
                          : language.name}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Language;
